import { getPlaceInfoBasic } from '@shared/core/places';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useFirstScheduleItem } from './useFirstScheduleItem';
import { useEffect, useState } from 'react';
import { useTranslation } from '@shared/core';

// Given any date (in milliseconds), make it 4pm
function setFourPm(milliseconds: number): number;
function setFourPm(milliseconds: number | undefined): number | undefined;
function setFourPm(milliseconds: number | undefined): number | undefined {
  if (!milliseconds) return undefined;
  const date = new Date(milliseconds);
  date.setHours(16, 0, 0, 0);
  return date.getTime();
}

export type PrimaryEventLocation = {
  Venue: string;
  Address: string;
  City: string;
  CityState: string;
  CityST: string;
  utcMilliseconds: number;
  timezone: string | undefined;
};

// Precedence (higher on top):
// VENUE/NAME   LOCATION     DATE       TIME
// VenueInfo    VenueInfo
// Event[0]     Event[0]     Event[0]   Event[0] <- Timezone from Event
//              Location     Finalized  4pm      <- Location is not guaranteed
// Placeholder  Placeholder  Today      4pm
export function usePrimaryEventLocation(): { location: PrimaryEventLocation; loading: boolean } {
  const { tq } = useTranslation('stationery');
  const [location, setLocation] = useState<PrimaryEventLocation>({
    Venue: tq`Your Venue`,
    Address: '8 Joy Lane',
    City: 'Seattle',
    CityState: 'Seattle, Washington',
    CityST: 'Seattle, WA',
    utcMilliseconds: setFourPm(new Date().getTime()),
    timezone: undefined,
  });
  const { eventHandle, eventInfo, loading: eventLoading } = useEventInfo();
  const { firstScheduleItem: scheduleItem, loading: scheduleLoading } = useFirstScheduleItem(eventHandle ?? '');
  const { venueInfo, loading: venueLoading } = useEventPlaces();
  const loading = eventLoading || scheduleLoading || venueLoading;
  const [gettingPlaceInfo, setGettingPlaceInfo] = useState(true);

  useEffect(() => {
    if (loading) return;

    const placeId = venueInfo.googlePlaceId ?? scheduleItem?.placeId ?? eventInfo?.locationInfo?.placeId;
    if (!placeId) {
      // Covers the !eventInfo (public gallery case)
      setGettingPlaceInfo(false);
      return;
    }

    void (async () => {
      const place = await getPlaceInfoBasic(placeId);
      setGettingPlaceInfo(false);
      if (!place) return;

      const addr = (type: string) => place.addressComponents.find(c => c.types.includes(type)) ?? { longText: '', shortText: '' };
      const Venue = venueInfo.name ?? scheduleItem?.location?.split(', ')[0] ?? tq`Your Venue`;
      // prettier-ignore
      const [Address, locality] = addr('route').longText
        ? (place.formattedAddress ?? '').split(', ')
        : [ // ex: ['King County', 'Seattle, WA']
          addr('administrative_area_level_2').longText,
          `${addr('locality').longText}, ${addr('administrative_area_level_1').shortText}`
        ];
      const country = addr('country').shortText;
      const City = addr('locality').longText;
      const state = addr('administrative_area_level_1');
      const CityState = country === 'US' ? `${City}, ${state.longText}` : (locality ?? '');
      const CityST = country === 'US' ? `${City}, ${state.shortText}` : (locality ?? '');

      // prettier-ignore
      const utcMilliseconds =
        scheduleItem?.startTime?.milliseconds
        ?? setFourPm(eventInfo?.finalizedDate?.milliseconds ?? undefined)
        ?? setFourPm(new Date().getTime())
      const timezone = scheduleItem?.startTime?.timezone;

      setLocation({ Venue, Address, City, CityState, CityST, utcMilliseconds, timezone });
    })();
  }, [eventInfo, loading, scheduleItem, venueInfo, tq]);

  return { location, loading: loading || gettingPlaceInfo };
}
